import { useToast } from '@app/core/notifications';
import { pushTo } from '@app/core/routing';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import {
  SocialExportListConnectionsQueryParamsPlatformEnumKey,
  socialExportGetConnectionByIdQueryKey,
  socialExportGetConnectionSettingsQueryKey,
  socialExportListConnectionsQueryKey,
  useSocialExportUpdateConnection,
} from '@mi-front/api-v2';
import { useQueryClient } from '@tanstack/react-query';
import { defineMessage, useIntl } from 'react-intl';

const settingsUpdatedMessage = defineMessage({
  id: 'socialExport.settings.updated',
  defaultMessage: 'Configuración actualizada correctamente',
});

const BY_STATION_CONNECTIONS_URL = '/social-export/v1/connections/by-station/:station';

type Params = {
  connectionId: string;
  platform: SocialPlatform;
  name: string;
  stationIds: string[];
};

/**
 * Saves connection settings (platform defaults + name + stations) and invalidates related caches.
 */
export function useUpdateConnectionSettings({
  connectionId,
  platform,
  name,
  stationIds,
}: Params) {
  const { $t } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const queryClient = useQueryClient();

  const mutation = useSocialExportUpdateConnection({
    mutation: {
      onSuccess: () => {
        // Navigate first so QueryView does not flash a loader while caches refetch.
        pushTo(`/admin/social-export/${platform.id.toLowerCase()}?connectionId=${connectionId}`);

        notifySuccess($t(settingsUpdatedMessage));

        void Promise.all([
          queryClient.invalidateQueries({
            queryKey: socialExportGetConnectionSettingsQueryKey(connectionId),
          }),
          queryClient.invalidateQueries({
            queryKey: socialExportGetConnectionByIdQueryKey(connectionId),
          }),
          queryClient.invalidateQueries({
            queryKey: socialExportListConnectionsQueryKey({
              platform: platform.id as SocialExportListConnectionsQueryParamsPlatformEnumKey,
            }),
          }),
          // Publish flows list connections by station; keep them in sync after reassignment.
          queryClient.invalidateQueries({
            predicate: (query) => {
              const meta = query.queryKey[1];
              return (
                typeof meta === 'object' &&
                meta !== null &&
                'url' in meta &&
                (meta as { url: string }).url === BY_STATION_CONNECTIONS_URL
              );
            },
          }),
        ]);
      },
    },
  });

  const handleUpdate = async (data: unknown) => {
    // API requires minLength 1 for name; omit when empty so the field is left unchanged.
    const trimmedName = name.trim();
    const settings = (data ?? {}) as Record<string, unknown>;

    try {
      await mutation.mutateAsync({
        id: connectionId,
        data: {
          ...settings,
          stationIds,
          ...(trimmedName ? { name: trimmedName } : {}),
        },
      });
    } catch (error: unknown) {
      notifyError((error as Error).message);
    }
  };

  return { handleUpdate, mutation };
}
